import { Application, ISigninSettings } from "ktm-ui/core";
import { ISessionResult } from "ktm-ui/core/AppSession";
import React from "react";
import MigrationMessageScreen from "./MigrationMessageScreen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

export interface ISigninProps {
  app: Application;
  done: (result: ISessionResult) => void;
  token?: string;
  settings: ISigninSettings;
}

export default class Signin extends React.Component<
  ISigninProps,
  {
    mode: string;
    username: string;
    password: string;
    showPassword: boolean;
    confirmPassword: string;
    pwdToken: string;
    error: string;
    busy: boolean;
    requires2FA: boolean;
    otp: string;
  }
> {
  supportLinkLabel: string;
  supportLinkHref: string;
  usernameLabel: string;
  submitLabel: string;
  registerLabel: string;
  forgotPasswordLabel: string;
  passwordResetInstruction: string;
  resetSentMessage: string;
  logoUrl: string;

  otpRefs = [
    React.createRef<HTMLInputElement>(),
    React.createRef<HTMLInputElement>(),
    React.createRef<HTMLInputElement>(),
    React.createRef<HTMLInputElement>(),
    React.createRef<HTMLInputElement>(),
    React.createRef<HTMLInputElement>(),
  ];

  constructor(props) {
    super(props);
    var mode = "login";
    let pwdToken;

    if (props.token) {
      pwdToken = props.token;
      mode = "reset";
    }

    this.state = {
      mode,
      username: "",
      password: "",
      showPassword: false,
      confirmPassword: "",
      pwdToken,
      error: "",
      busy: false,
      requires2FA: false,
      otp: "",
    };

    let settings = this.props.settings;
    this.supportLinkLabel         = settings.supportLinkLabel;
    this.supportLinkHref          = settings.supportLinkHref;
    this.usernameLabel            = settings.usernameLabel;
    this.submitLabel              = settings.submitLabel;
    this.registerLabel            = settings.registerLabel;
    this.forgotPasswordLabel      = settings.forgotPasswordLabel;
    this.resetSentMessage         = settings.resetSentMessage;
    this.logoUrl                  = settings.logoUrl;
    this.passwordResetInstruction = settings.passwordResetInstruction;

    this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
  }

  render() {
    return <div className="RT-Login__container">{this.renderContent()}</div>;
  }

  submit = async (e) => {
    e.preventDefault();
    let app = this.props.app;
    try {
      this.setState({ busy: true });
      let result = await app.session.attemptSignin(
        this.state.username.trim(),
        this.state.password
      );
      this.setState({ busy: false });
      if (result.requires2FA) {
        this.setState({ requires2FA: true, error: "" });
        setTimeout(() => this.otpRefs[0].current?.focus(), 50);
      } else if (result.success) {
        return this.props.done(result);
      } else {
        if (result.error === "Inactive Enrollment Status") {
          this.setState({ error: "Inactive Enrollment Status", busy: false });
        } else if (result.error === "Set password") {
          this.setState({ mode: "forgot" });
        }
        this.setState({ error: result.error });
      }
    } catch (e) {
      this.setState({ error: this.getErrorMessage(e), busy: false });
    }
  };

  // OTP input handlers
  handleOtpInput = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "");
    e.target.value = val.slice(-1);

    const digits = this.otpRefs.map(r => r.current?.value || "");
    this.setState({ otp: digits.join("") });

    if (val && index < 5) {
      this.otpRefs[index + 1].current?.focus();
    }

    const full = digits.map((d, i) => i === index ? val.slice(-1) : d).join("");
    if (full.length === 6) {
      this.setState({ otp: full }, () => this.handle2FASubmit(full));
    }
  };

  handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !this.otpRefs[index].current?.value && index > 0) {
      this.otpRefs[index - 1].current?.focus();
    }
    if (e.key === "Enter" && this.state.otp.length === 6) {
      this.handle2FASubmit(this.state.otp);
    }
  };

  handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    pasted.split("").forEach((ch, i) => {
      if (this.otpRefs[i]?.current) this.otpRefs[i].current!.value = ch;
    });
    const full = pasted.padEnd(6, "").slice(0, 6);
    this.setState({ otp: full });
    const lastIndex = Math.min(pasted.length - 1, 5);
    this.otpRefs[lastIndex].current?.focus();
    if (pasted.length === 6) {
      this.setState({ otp: full }, () => this.handle2FASubmit(full));
    }
  };

  handle2FASubmit = async (token: string) => {
    let app = this.props.app;
    this.setState({ busy: true, error: "" });
    try {
      let result = await app.session.verifyTwoFactorCode(
        this.state.username,
        token
      );
      this.setState({ busy: false });

      if (result.errorCode === "OTP_LOCKOUT" || result.errorCode === "SESSION_EXPIRED") {
        // Force back to login screen
        this.setState({
          requires2FA: false,
          otp: "",
          error: result.error || "Too many failed attempts. Please sign in again."
        });
        this.otpRefs.forEach(r => { if (r.current) r.current.value = ""; });
      } else if (result.success) {
        return this.props.done(result);
      } else {
        // Wrong code — clear boxes and refocus
        this.setState({ otp: "", error: result.error || "Verification failed." });
        this.otpRefs.forEach(r => { if (r.current) r.current.value = ""; });
        setTimeout(() => this.otpRefs[0].current?.focus(), 50);
      }
    } catch (e) {
      this.setState({ busy: false, otp: "", error: this.getErrorMessage(e) });
      this.otpRefs.forEach(r => { if (r.current) r.current.value = ""; });
      setTimeout(() => this.otpRefs[0].current?.focus(), 50);
    }
  };

  handleResend2FA = async (e) => {
    e.preventDefault();
    let app = this.props.app;
    this.setState({ busy: true, error: "" });
    try {
      let result = await app.session.resendTwoFactorCode(this.state.username);
      this.setState({ busy: false });
      if (!result.success) {
        this.setState({ error: result.error || "Failed to resend code." });
      }
    } catch (e) {
      this.setState({ busy: false, error: this.getErrorMessage(e) });
    }
  };

  handleBackToLogin = (e) => {
    e.preventDefault();
    this.setState({
      requires2FA: false,
      otp: "",
      error: "",
      password: "",
    });
    this.otpRefs.forEach(r => { if (r.current) r.current.value = ""; });
  };

  getErrorMessage(e: any): string {
    if (!e.body || !e.body.error || !e.body.error.message) {
      return "An error occurred, unable to " + this.submitLabel;
    }
    return e.body.error.message;
  }

  requestToken = (requestType: string) => async (e) => {
    e.preventDefault();
    let app = this.props.app;
    try {
      this.setState({ busy: true });
      let result = await app.session.requestToken(this.state.username);
      if (result.success) {
        this.setState({ mode: requestType, busy: false, error: "" });
      } else {
        this.setState({ error: result.error, busy: false });
      }
    } catch (e) {
      this.setState({ error: this.getErrorMessage(e), busy: false });
    }
  };

  signup = (e) => { e.preventDefault(); };

  resetPassword = async (e) => {
    e.preventDefault();
    let app = this.props.app;
    try {
      this.setState({ busy: true });
      let result = await app.session.resetPassword(this.props.token, this.state.password);
      if (!result.success) {
        this.setState({ busy: false, error: result.error });
        return;
      }
      this.setState({ mode: "login", busy: false, password: "", error: "" }, () => {
        window.location.href = "/ops";
      });
    } catch (e) {
      this.setState({ error: this.getErrorMessage(e), busy: false });
    }
  };

  handleInputChange = (e) => {
    let name: "username" | "password" | "confirmPassword" = e.target.name;
    this.setState({ [name]: e.target.value } as any);
  };

  togglePasswordVisibility() {
    this.setState(prevState => ({ showPassword: !prevState.showPassword }));
  }

  handleRegisterClick = (e) => {
    e.preventDefault();
    this.setState({ mode: "register", error: "", password: "", confirmPassword: "" });
  };

  handleForgotClick = (e) => {
    e.preventDefault();
    this.setState({ mode: "forgot", error: "", password: "", confirmPassword: "" });
  };

  handleReturnToLoginClick = (e) => {
    e.preventDefault();
    this.setState({ mode: "login", error: "", password: "", confirmPassword: "" });
  };

  renderContent() {
    if (this.state.requires2FA) {
      return this.renderTwoFactorScreen();
    }
    var mode = this.state.mode;
    if (mode == "login")                return this.renderLoginScreen();
    else if (mode == "register")        return this.renderRegister();
    else if (mode == "forgot")          return this.renderForgot();
    else if (mode == "requestregister") return this.renderRequestSent("requestregister");
    else if (mode == "requestforgot")   return this.renderRequestSent("requestforgot");
    else if (mode == "reset")           return this.renderReset();
    else if (mode == "inactive_enrollment") return <MigrationMessageScreen />;
  }

  renderTwoFactorScreen() {
    const { busy, error } = this.state;
    return (
      <div className="RT-Login__form">
        {this.renderLogo()}
        <div className="RT-Login__title" style={{ fontWeight: 500, fontSize: "0.8rem", textAlign: "center", margin: "auto", letterSpacing: "1px" }}>
          ADMIN PORTAL
        </div>
        <div style={{ textAlign: "center", color: "#6b7280", fontSize: "0.85rem", marginTop: 12, marginBottom: 20 }}>
          A 6-digit verification code has been sent to your email.
        </div>

        {error && <div className="RT-Login__error">{error}</div>}

        {/* 6-box OTP input */}
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 20 }}>
          {this.otpRefs.map((ref, i) => (
            <input
              key={i}
              ref={ref}
              type="text"
              inputMode="numeric"
              maxLength={1}
              autoComplete={i === 0 ? "one-time-code" : "off"}
              onChange={e => this.handleOtpInput(i, e)}
              onKeyDown={e => this.handleOtpKeyDown(i, e)}
              onPaste={i === 0 ? this.handleOtpPaste : undefined}
              disabled={busy}
              style={{
                width: 44,
                height: 52,
                textAlign: "center",
                fontSize: 22,
                fontWeight: 600,
                border: "1px solid #d1d5db",
                borderRadius: 8,
                outline: "none",
              }}
            />
          ))}
        </div>

        <div className="RT-Login__button-container">
          {busy ? (
            <button disabled className="RT-Button RT-Button--primary RT-Button--block">
              Verifying ...
            </button>
          ) : (
            <button
              className="RT-Button RT-Button--primary RT-Button--block"
              disabled={this.state.otp.length < 6}
              onClick={() => this.handle2FASubmit(this.state.otp)}
            >
              Verify
            </button>
          )}
        </div>

        <div className="RT-Login__link-container" style={{ flexDirection: "column", gap: 8 }}>
          <a href="" onClick={this.handleResend2FA} className="RT-Login__link" style={{ fontSize: "0.8rem" }}>
            Resend code
          </a>
          <a href="" onClick={this.handleBackToLogin} className="RT-Login__link" style={{ fontSize: "0.8rem" }}>
            ← Back to sign in
          </a>
        </div>
      </div>
    );
  }

  renderLoginScreen() {
    let assistanceLink = this.supportLinkHref
      ? <a href={this.supportLinkHref}>{this.supportLinkLabel}</a>
      : this.supportLinkLabel;

    let register = this.registerLabel && (
      <a href="" className="RT-Login__link" onClick={this.handleRegisterClick}>
        {this.registerLabel}
      </a>
    );

    let forgot = this.forgotPasswordLabel && (
      <a href="" onClick={this.handleForgotClick} className="RT-Login__link" style={{ fontSize: "0.8rem" }}>
        {this.forgotPasswordLabel}
      </a>
    );

    let submitButton = this.state.busy ? (
      <button disabled type="submit" className="RT-Button RT-Button--primary RT-Button--block">
        Logging in ...
      </button>
    ) : (
      <button type="submit" className="RT-Button RT-Button--primary RT-Button--block">
        {this.submitLabel}
      </button>
    );

    return (
      <div className="RT-Login__form">
        {this.renderLogo()}
        <div className="RT-Login__title" style={{ fontWeight: 500, fontSize: "0.8rem", textAlign: "center", margin: "auto", letterSpacing: "1px" }}>
          ADMIN PORTAL
        </div>
        <form onSubmit={this.submit}>
          {this.renderError()}
          <label className="RT-Login__label">{this.usernameLabel}</label>
          <input type="text" name="username" className="RT-Login__input"
            value={this.state.username} onChange={this.handleInputChange} />
          <label className="RT-Login__label">Password</label>
          <div className="password-container">
            <input
              type={this.state.showPassword ? "text" : "password"}
              name="password"
              className="login-input"
              value={this.state.password}
              onChange={this.handleInputChange}
            />
            <FontAwesomeIcon
              icon={this.state.showPassword ? faEye : faEyeSlash}
              className="toggle-password"
              onMouseDown={() => this.setState({ showPassword: true })}
              onMouseUp={() => this.setState({ showPassword: false })}
              onMouseLeave={() => this.setState({ showPassword: false })}
            />
          </div>
          <div className="RT-Login__button-container">{submitButton}</div>
          <div className="RT-Login__link-container">
            {/*{register}*/}
            {forgot}
          </div>
          <div className="RT-Login__help">
            <div>Need assistance?</div>
            <div>{assistanceLink}</div>
          </div>
        </form>
      </div>
    );
  }

  renderError() {
    if (this.state.error) {
      if (this.state.error === "Invalid permission")
        return (
          <div className="RT-Login__error">
            This user does not have Plan Administration access. Looking to submit claims or check on your balance? Sign into the{" "}
            <b><a className="RT-Login__link" href="http://employee.nationalhealthclaim.com"> Employee Portal</a></b>.
          </div>
        );
      return <div className="RT-Login__error">{this.state.error}</div>;
    }
    return null;
  }

  renderRegister() {
    return (
      <div className="RT-Login__form">
        {this.renderLogo()}
        <div className="RT-Login__title" style={{ fontWeight: 500, fontSize: "0.8rem", textAlign: "center", margin: "auto", letterSpacing: "1px", color: "#333" }}>
          ADMIN PORTAL
        </div>
        <form onSubmit={this.signup}>
          {this.renderError()}
          <label className="RT-Login__label">Enter your email address to set up your password.</label>
          <input type="text" name="username" className="RT-Login__input"
            value={this.state.username} onChange={this.handleInputChange} />
          <div className="RT-Login__button-container">
            <button type="submit" className="RT-Button RT-Button--primary RT-Button--block">Continue</button>
          </div>
          <div className="RT-Login__link-container">
            <a href="" onClick={this.handleReturnToLoginClick} className="RT-Login__link">
              Return to {this.submitLabel}
            </a>
          </div>
        </form>
      </div>
    );
  }

  renderForgot() {
    let submitButton = this.state.busy ? (
      <button disabled type="submit" className="RT-Button RT-Button--primary RT-Button--block">Working ...</button>
    ) : (
      <button type="submit" className="RT-Button RT-Button--primary RT-Button--block">Continue</button>
    );
    return (
      <div className="RT-Login__form">
        {this.renderLogo()}
        <div className="RT-Login__title" style={{ fontWeight: 500, fontSize: "0.8rem", textAlign: "center", margin: "auto", letterSpacing: "1px", color: "#333" }}>
          ADMIN PORTAL
        </div>
        <form onSubmit={this.requestToken("requestforgot")}>
          {this.renderError()}
          <label className="RT-Login__label">Enter your email address to set up your password</label>
          <input type="text" name="username" className="RT-Login__input"
            value={this.state.username} onChange={this.handleInputChange} />
          <div className="RT-Login__button-container">{submitButton}</div>
          <div className="RT-Login__link-container">
            <a href="" onClick={this.handleReturnToLoginClick} className="RT-Login__link">
              Return to {this.submitLabel}
            </a>
          </div>
        </form>
      </div>
    );
  }

  renderReset() {
    let submitButton = this.state.busy ? (
      <button disabled type="submit" className="RT-Button RT-Button--primary RT-Button--block">Working...</button>
    ) : (
      <button type="submit" className="RT-Button RT-Button--primary RT-Button--block">Set Password</button>
    );
    return (
      <div className="RT-Login__form">
        {this.renderLogo()}
        <form onSubmit={this.resetPassword}>
          {this.renderError()}
          <div className="RT-Login__label">Enter new password</div>
          <input type="password" name="password" className="RT-Login__input"
            value={this.state.password} onChange={this.handleInputChange} />
          <div className="RT-Login__label">Confirm password</div>
          <input type="password" name="confirmPassword" className="RT-Login__input"
            value={this.state.confirmPassword} onChange={this.handleInputChange} />
          <div className="RT-Login__button-container">{submitButton}</div>
          <div className="RT-Login__link-container">
            <a href="" onClick={this.handleReturnToLoginClick} className="RT-Login__link">
              Return to {this.submitLabel}
            </a>
          </div>
        </form>
      </div>
    );
  }

  renderLogo() {
    if (this.logoUrl) {
      return <div className="RT-Login__logo"><img src={this.logoUrl} /></div>;
    }
    return null;
  }

  renderRequestSent(requestType: string) {
    return (
      <div className="RT-Login__form">
        {this.renderLogo()}
        <form onSubmit={this.requestToken(requestType)}>
          {this.renderError()}
          <div className="RT-Login__label">
            If you have entered a registered email address you will receive an email containing the instructions to set up your password.
          </div>
          <div className="RT-Login__link-container">
            <a href="" onClick={this.handleReturnToLoginClick} className="RT-Login__link">
              Return to {this.submitLabel}
            </a>
          </div>
        </form>
      </div>
    );
  }
}